import { Analytics } from '@cobliteam/analytics';
import { logoutRequest } from '@cobliteam/store/src/Login';
import { changeUserFleetRequest } from '@cobliteam/store/src/Login';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import DropdownMenu from '@cobliteam/ui/components/DropdownMenu';
import Icon from '@cobliteam/ui/icons';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './AppFleetAvatar';

interface Fleet {
  name: string;
  id: string;
}

interface FleetSwitcherProps {
  fleets: Fleet[];
  currentFleet: Fleet;
}

function AppFleetSwitcher({ fleets = [], currentFleet = { name: '', id: '' } }: FleetSwitcherProps) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const handleChangeFleet = (fleetId: string, fleetName: string) => {
    dispatch(changeUserFleetRequest({ id: fleetId, name: fleetName }));
    Analytics.track('FleetSwitcher - Switch - Fleet', { fleetId, fleetName });
  };

  const handleLogout = () => {
    dispatch(logoutRequest());
    Analytics.track('FleetSwitcher - Click - Logout');
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      Analytics.track('FleetSwitcher - Open');
    }
    setOpen(isOpen);
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
          <DropdownMenu.Trigger asChild>
            <SidebarMenuButton size='lg' className=' data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'>
              <div className='flex aspect-square items-center justify-center rounded-lg text-sidebar-primary-foreground'>
                <Avatar className='size-9 rounded-md'>
                  <AvatarImage src={`https://assets.cobli.co/fleets/${currentFleet.id}/logo.png`} />
                  <AvatarFallback>
                    <Icon name='gallery-vertical-end' className='size-3' />
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-semibold'>{currentFleet.name}</span>
              </div>
              <Icon name='chevrons-up-down' className='ml-auto' />
            </SidebarMenuButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            className='-mt-3 w-80 rounded-lg border-gray-11 bg-sidebar pt-2 pb-2 text-sidebar-foreground'
            alignOffset={-20}
            align='start'
            side='bottom'
            collisionPadding={{ left: 10 }}
            sideOffset={20}
          >
            <div className='flex flex-col px-2 pt-2 text-gray-8 text-xs'>Troca de frotas</div>

            <SidebarMenu>
              <div className='flex max-h-[calc(100vh-200px)] flex-col gap-2 overflow-y-auto'>
                {fleets
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((fleet) => {
                    const isActive = currentFleet.id === fleet.id;
                    const fleetLogo = `https://assets.cobli.co/fleets/${fleet.id}/logo.png`;
                    return (
                      <SidebarMenuItem key={fleet.name} className='px-1'>
                        <SidebarMenuButton onClick={() => handleChangeFleet(fleet.id, fleet.name)} className='text-xs capitalize' isActive={isActive}>
                          <Avatar className='size-7 rounded-sm'>
                            <AvatarImage src={fleetLogo} />
                            <AvatarFallback className='bg-primary-12 p-1 text-sidebar-primary-foreground opacity-80'>
                              <Icon name='gallery-vertical-end' className='size-3' />
                            </AvatarFallback>
                          </Avatar>
                          <span className='font-medium text-sm'>{fleet.name.toLocaleLowerCase()}</span>
                          {isActive && <Icon name='check' className='ml-auto' />}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
              </div>

              <SidebarSeparator />
              <SidebarMenuItem>
                <Link
                  to='/config'
                  onClick={() => {
                    Analytics.track('FleetSwitcher - Click - Settings');
                    setOpen(false);
                  }}
                >
                  <SidebarMenuButton>
                    <Icon name='settings' className='size-4' />
                    Configurações da frota
                  </SidebarMenuButton>
                </Link>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={handleLogout}>
                  <Icon name='log-out' className='size-4' />
                  Sair
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </DropdownMenu.Content>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export default AppFleetSwitcher;
